import { ChangeDetectionStrategy, Component, ElementRef, forwardRef, HostListener, Input, OnInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { applyLimits, caretFromTokens, computeLocaleSeps, countTokensBeforeCaret, sanitize, splitDecimal, updateLimitHint } from '@shared/helpers/numeric-input.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';

@Component({
    selector: 'custom-number-input',
    templateUrl: './custom-number-input.component.html',
    styleUrls: ['./custom-number-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CustomNumberInputComponent),
            multi: true,
        },
    ],
})
export class CustomNumberInputComponent implements ControlValueAccessor, OnInit {
    @ViewChild('inputEl', { static: true }) inputEl: ElementRef<HTMLInputElement>;
    @Input() locale: string = 'it-IT';
    @Input() allowNegative = true;
    @Input() textEnd: boolean = true;
    @Input() maxFractionDigits = 2;
    @Input() maxIntegerDigits = 13;
    @Input() placeholder = 'Inserisci';
    @Input() prefix: string;
    @Input() suffix: string;
    @Input() disabled = false;

    limitHint: string | null = null;

    private onChange: (v: number | null) => void = () => {};
    private onTouched: () => void = () => {};
    private _numericValue: number | null = null;
    private _decimalSep: '.' | ',' = ',';
    private _groupSep: string = '.';

    get numericValue(): number | null {
        return this._numericValue;
    }

    ngOnInit(): void {
        const thousandSeparator = computeLocaleSeps(this.locale);
        this._decimalSep = thousandSeparator.decimalSep;
        this._groupSep = thousandSeparator.groupSep;
    }

    writeValue(value: number | null): void {
        this._numericValue = isNilOrEmpty(value) ? null : value;

        if (this._numericValue === null) {
            this._setNativeValue('');
            return;
        }

        const formatted = this._format(this._numericValue);
        this._setNativeValue(formatted);
    }

    registerOnChange(fn: (v: number | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
        if (this.inputEl?.nativeElement) {
            this.inputEl.nativeElement.disabled = isDisabled;
        }
    }

    @HostListener('input', ['$event'])
    onInput(ev: Event): void {
        const el = ev.target as HTMLInputElement;
        const raw = el.value ?? '';
        const caret = el.selectionStart ?? raw.length;
        const tokenCount = countTokensBeforeCaret(raw, caret, this._decimalSep);
        const cleaned = this._cleanTyping(raw);
        const limited = applyLimits(cleaned, this.maxIntegerDigits, this.maxFractionDigits, this._decimalSep);
        this.limitHint = updateLimitHint(limited, this.maxIntegerDigits);

        const n = this._parseToNumber(limited);
        if (el.value !== limited) {
            el.value = limited;
            const newCaret = caretFromTokens(limited, tokenCount, this._decimalSep);
            queueMicrotask(() => el.setSelectionRange(newCaret, newCaret));
        }

        const committable = !(this.maxFractionDigits > 0 && limited.endsWith(this._decimalSep));
        if (committable) {
            this._numericValue = n;
            this.onChange(n);
        }
    }

    @HostListener('focusin')
    onFocusIn(): void {
        if (this._numericValue === null) return;

        const plain = this._formatNoGroup(this._numericValue);
        this._setNativeValue(plain);
    }

    private _formatNoGroup(n: number): string {
        return new Intl.NumberFormat(this.locale, {
            minimumFractionDigits: 0,
            maximumFractionDigits: this.maxFractionDigits,
            useGrouping: false,
        }).format(n);
    }

    @HostListener('focusout')
    onFocusOut(): void {
        this.onTouched();

        if (this._numericValue === null) {
            this._setNativeValue('');
            return;
        }

        const formatted = this._format(this._numericValue);
        this._setNativeValue(formatted);
    }

    private _setNativeValue(v: string): void {
        if (this.inputEl?.nativeElement) {
            this.inputEl.nativeElement.value = v;
        }
    }

    private _cleanTyping(value: string): string {
        const { negative, body } = sanitize(value, this.allowNegative, this._groupSep, this._decimalSep, this.maxFractionDigits);
        const { intPart, fracPart, separatorChar } = splitDecimal(body, this._decimalSep);
        const sign = negative ? '-' : '';
        if (!separatorChar) {
            return `${sign}${intPart}`;
        }

        return `${sign}${intPart}${separatorChar}${fracPart}`;
    }

    private _parseToNumber(value: string): number | null {
        const { negative, body } = sanitize(value, this.allowNegative, this._groupSep, this._decimalSep, this.maxFractionDigits);
        if (!body) {
            return null;
        }

        const { intPart, fracPart, separatorChar } = splitDecimal(body, this._decimalSep);
        if (!intPart) {
            return null;
        }

        const normalized = separatorChar && fracPart.length ? `${intPart}.${fracPart}` : intPart;
        const n = Number.parseFloat(normalized);
        if (!Number.isFinite(n)) {
            return null;
        }

        const signed = negative ? -n : n;
        // Guard against float64 precision loss: if the value cannot be represented exactly as an
        // integer number of minor units, keep the last safe value instead of committing a corrupted one.
        const minorUnits = Math.round(signed * 10 ** this.maxFractionDigits);
        if (!Number.isSafeInteger(minorUnits)) {
            return this._numericValue;
        }

        return signed;
    }

    private _format(n: number): string {
        return new Intl.NumberFormat(this.locale, {
            minimumFractionDigits: 0,
            maximumFractionDigits: this.maxFractionDigits,
            useGrouping: true,
        }).format(n);
    }
}
